import { useEffect } from "react";
// Presence cursor / avatar palette. A user's colour is derived from their id, so
// the same person keeps one colour across every canvas (SOLVE, VSM, Hoshin).
// Strict `#RRGGBB` values so the DS `awarenessColor` honours them rather than
// falling back to the neutral cyan.
const PRESENCE_COLORS = [
    "#0891b2",
    "#059669",
    "#7c3aed",
    "#d97706",
    "#dc2626",
    "#0f766e",
    "#2563eb",
    "#be185d",
];
/** Stable colour for a user id: a small string hash into `PRESENCE_COLORS`. */
export function presenceColorForUser(userId) {
    let hash = 0;
    for (let i = 0; i < userId.length; i += 1) {
        hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
    }
    return PRESENCE_COLORS[hash % PRESENCE_COLORS.length] ?? PRESENCE_COLORS[0];
}
/**
 * Publish the local user into canvas presence awareness so remote peers can
 * label and colour this client's cursor and selection. Writes the reserved
 * `user` field (id, display name, deterministic colour); the DS cursor publisher
 * and `PresenceCursors` read it back.
 *
 * No-ops until an awareness handle is available (it may arrive on a later
 * render — the handle is a dependency, so the field publishes as soon as it
 * does). Clears the owned `user` field when identity is lost (e.g. logout /
 * session expiry while still mounted) and on unmount / identity change: the
 * provider is ref-counted and can outlive this component (another binder, or a
 * StrictMode double-mount, keeps it alive), so relying on `provider.destroy()`
 * alone can leave a stale identity visible to peers.
 */
export function usePublishCanvasPresence(awareness, user) {
    useEffect(() => {
        if (!awareness)
            return;
        if (!user?.id) {
            awareness.setLocalStateField("user", null);
            return;
        }
        awareness.setLocalStateField("user", {
            id: user.id,
            name: user.name ?? user.id,
            color: presenceColorForUser(user.id),
        });
        return () => awareness.setLocalStateField("user", null);
    }, [awareness, user?.id, user?.name]);
}
//# sourceMappingURL=presence.js.map